import React, { useEffect, useRef, useState, useMemo } from 'react';
import * as d3 from 'd3';

const HorizonView = () => {
  const svgRef = useRef();
  const containerRef = useRef();
  const [containerWidth, setContainerWidth] = useState(0);
  
  const data = useMemo(() => ({
    name: "Medical School",
    children: [{
      name: "Foundation Year 1",
      children: [{
        name: "Foundation Year 2",
        children: [{
          name: "Internal Medicine Training",
          children: [{
            name: "Cardiology ST3",
            children: [{
              name: "Cardiology ST4",
              children: [{
                name: "Cardiology ST5",
                children: [{
                  name: "Cardiology ST6",
                  children: [{
                    name: "Cardiology ST7",
                    children: [
                      { name: "Interventional Cardiology" },
                      { name: "Electrophysiology" },
                      { name: "Heart Failure" },
                      { name: "Imaging" }
                    ]
                  }]
                }]
              }]
            }]
          }]
        }]
      }]
    }]
  }), []);

  // Add resize observer
  useEffect(() => {
    const container = containerRef.current;
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    if (container) {
      resizeObserver.observe(container);
    }

    return () => {
      if (container) {
        resizeObserver.unobserve(container);
      }
    };
  }, []);

  useEffect(() => {
    if (containerWidth === 0) return;
    
    const width = containerWidth - 30; // Account for padding
    const height = 600; // Reduced height
    const margin = { top: 20, right: 0, bottom: 60, left: 0 }; // Reduced margins

    // Clear existing SVG
    d3.select(svgRef.current).selectAll("*").remove();

    // Create new SVG
    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height);

    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Modified tree layout for diagonal orientation
    const treeLayout = d3.tree()
      .size([width - margin.left - margin.right, height - margin.top - margin.bottom])
      .separation((a, b) => (a.parent === b.parent ? 1 : 1.5)); // Reduced separation

    // Create hierarchy
    const root = d3.hierarchy(data);
    const treeData = treeLayout(root);

    // Create diagonal links
    const link = g.selectAll(".link")
      .data(treeData.links())
      .enter().append("path")
      .attr("class", "link")
      .attr("d", d3.linkHorizontal()
        .x(d => d.x) // Swap x and y coordinates
        .y(d => height - margin.top - margin.bottom - d.y)) // Invert y coordinate
      .style("fill", "none")
      .style("stroke", "#007BFF")
      .style("stroke-width", "1px");

    // Update node positioning for diagonal layout
    const node = g.selectAll(".node")
      .data(treeData.descendants())
      .enter().append("g")
      .attr("class", d => "node" + (d.children ? " node--internal" : " node--leaf"))
      .attr("transform", d => 
        `translate(${d.x},${height - margin.top - margin.bottom - d.y})`);

    // Add background rectangles for nodes
    node.append("rect")
      .attr("class", "node-box")
      .attr("y", -15) // Position above the center
      .attr("x", d => -(d.data.name.length * 8 + 16) / 2) // Center box for all nodes
      .attr("width", d => d.data.name.length * 8 + 16) // Dynamic width based on text length
      .attr("height", 30)
      .attr("rx", 5) // Rounded corners
      .attr("ry", 5)
      .style("fill", "white")
      .style("stroke", "#007BFF")
      .style("stroke-width", "1px");

    // Add node labels with centered positioning
    node.append("text")
      .attr("x", 0) // Center text for all nodes
      .style("text-anchor", "middle")
      .style("font-size", "14px")
      .style("font-family", "Arial")
      .style("dominant-baseline", "middle") // Ensure vertical centering
      .text(d => d.data.name)
      .style("pointer-events", "none"); // Prevent text from interfering with hover

    // Helper function to get all descendants of a node
    const getDescendants = (node) => {
      let descendants = [];
      if (node.children) {
        node.children.forEach(child => {
          descendants.push(child);
          descendants = descendants.concat(getDescendants(child));
        });
      }
      return descendants;
    };

    // Updated hover effects
    node.selectAll(".node-box")
      .on("mouseover", function(event, d) {
        // Highlight current node
        d3.select(this)
          .style("fill", "#007BFF")
          .style("stroke", "gold");
        d3.select(this.parentNode).select("text")
          .style("fill", "white")
          .style("font-weight", "bold");

        // Highlight all descendant nodes
        const descendants = getDescendants(d);
        descendants.forEach(descendant => {
          const isTerminal = !descendant.children;
          d3.select(descendant.node)
            .select(".node-box")
            .style("fill", "#007BFF")
            .style("stroke", isTerminal ? "gold" : "#0056b3") // Terminal nodes get gold border
            .style("stroke-width", isTerminal ? "2px" : "1px"); // Make terminal borders thicker
          d3.select(descendant.node)
            .select("text")
            .style("fill", "white")
            .style("font-weight", "bold");
        });

        // Highlight connecting paths to descendants
        link.style("stroke", l => {
          const isDescendantPath = descendants.some(desc => 
            ((l.source === d) && (l.target === desc)) || 
            ((descendants.includes(l.source)) && (descendants.includes(l.target)))
          );
          return isDescendantPath ? "gold" : "#007BFF"; // Changed color to bright red
        })
        .style("stroke-width", l => {
          const isDescendantPath = descendants.some(desc => 
            ((l.source === d) && (l.target === desc)) || 
            ((descendants.includes(l.source)) && (descendants.includes(l.target)))
          );
          return isDescendantPath ? "2px" : "1px";
        });
      })
      .on("mouseout", function() {
        // Reset all nodes
        node.selectAll(".node-box")
          .style("fill", "white")
          .style("stroke", "#007BFF")
          .style("stroke-width", "1px"); // Reset stroke width
        node.selectAll("text")
          .style("fill", "black")
          .style("font-weight", "normal");

        // Reset all links
        link.style("stroke", "#007BFF")
            .style("stroke-width", "1px");
      });

    // Remove the circle nodes since we're using boxes
    node.selectAll("circle").remove();

  }, [containerWidth, data]); // Add both containerWidth and data as dependencies

  return (
    <div className="info-box horizon-view">
      <h3 className="horizon-view-title">Career Path Progression Map</h3>
      <div className="tree-container" ref={containerRef}>
        <svg ref={svgRef}></svg>
      </div>

      <style jsx>{`
        .info-box {
          background-color: #fff;
          border: 1px solid #ccc;
          border-radius: 8px;
          padding: 15px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          position: relative;
          transition: all 0.3s ease;
        }

        .horizon-view {
          width: 100%;
          margin: 0 auto;
          background: white;
          border-radius: 12px;
          padding: 2rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .horizon-view-title {
          text-align: center;
          margin-bottom: 12px;
          color: #333;
          font-size: 1.8rem;
        }

        .tree-container {
          padding: 10px; // Reduced padding
          border-radius: 8px;
          overflow: auto;
          width: 100%;
        }
        
      `}</style>
    </div>
  );
};

export default HorizonView;
